import React from "react";
import { ChevronRight, Edit, EditOutlined, PictureInPictureOutlined, Route, SvgIconComponent } from "@mui/icons-material";
import { Alert, Box } from "@mui/material";

import { NavigationButton } from "ui";

import { useFeatureFlag } from "fond/featureFlags";
import { useStackedNavigationContext } from "fond/widgets";

import FlyoutHeader from "./flyout/FlyoutHeader";
import { Gate, ModifyDataAction } from "./gating";

const ACTIONS: Record<ModifyDataAction, { title: string; description: string; icon: SvgIconComponent; screenId: string }> = {
  editLayers: {
    title: "Edit layers",
    description: "Draw and edit base data on the map",
    icon: EditOutlined,
    screenId: "extended-design-modifications-draw",
  },
  pathPreferences: {
    title: "Path preferences",
    description: "Prefer or avoid underground paths in the design",
    icon: Route,
    screenId: "extended-design-modifications-path-preferences",
  },
  reviewParcels: {
    title: "Review problematic parcels",
    description: "Find and remove parcels that overlap streets",
    icon: PictureInPictureOutlined,
    screenId: "identify-street-parcels",
  },
};

interface ModifyDataFlyoutProps {
  /** Whether each tool can be used now (see `BaseDataGating.modify`). */
  gates: Record<ModifyDataAction, Gate>;
  onClose(): void;
}

/** Map flyout listing the base data clean-up tools, each of which opens in the left panel. */
const ModifyDataFlyout: React.FC<ModifyDataFlyoutProps> = ({ gates, onClose }) => {
  const { open } = useStackedNavigationContext();
  const { value: isPathPreferencingEnabled } = useFeatureFlag("path_preferencing", false);
  const { value: isParcelReviewEnabled } = useFeatureFlag("street_parcel_identification");

  const isAvailable: Record<ModifyDataAction, boolean> = {
    editLayers: true,
    pathPreferences: isPathPreferencingEnabled,
    reviewParcels: isParcelReviewEnabled,
  };

  const handleOnOpen = (action: ModifyDataAction) => {
    onClose();
    open(ACTIONS[action].screenId);
  };

  return (
    <Box display="flex" flexDirection="column" height="100%" bgcolor="background.paper" data-testid="modify-data-flyout">
      <FlyoutHeader icon={Edit} title="Modify data" onClose={onClose} />
      <Box flexGrow={1} minHeight={0} overflow="auto" display="flex" flexDirection="column" gap={1.25} p={1.75}>
        <Alert severity="info" variant="compact" sx={{ mb: 1.25 }}>
          Tidy up your base data before designing. Each tool opens in the side panel.
        </Alert>
        {(Object.keys(ACTIONS) as ModifyDataAction[])
          .filter((action) => isAvailable[action])
          .map((action) => {
            const { title, description, icon: Icon } = ACTIONS[action];
            const gate = gates[action];
            return (
              <NavigationButton
                key={action}
                icon={<Icon />}
                title={title}
                subtitle={gate.reason ?? description}
                disabled={!gate.enabled}
                onClick={() => handleOnOpen(action)}
                rightElement={<ChevronRight color="action" />}
                sx={{ m: 0 }}
              />
            );
          })}
      </Box>
    </Box>
  );
};

export default ModifyDataFlyout;
